import { KvixIcon, StatusText, Text } from "@kvix/ui";
import {
  Box,
  Chip,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { format, isBefore } from "date-fns";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Stripe as ServerStripe } from "stripe";
import styled from "styled-components";
import { PaymentContext } from "../../../context";
import { useDefaultPaymentMethodId } from "../../../hooks";
import { CreditCardIssuerStyles } from "./Issuers";
import { CreditCardIcon } from "./Issuers/Icons";
import { CreditCardIssuer } from "./Issuers/issuers";

interface Props {
  paymentMethod: ServerStripe.PaymentMethod | string;
}

interface ContainerProps {
  compact?: boolean;
}

const Container = styled.div<ContainerProps>`
  display: grid;
  align-items: center;
  grid-auto-flow: column;

  grid-template-columns: ${(props) =>
    props.compact ? "auto 2.5fr 0.75fr auto" : "1fr 1fr auto auto"};
  grid-gap: ${(props) => (props.compact ? "16px" : "24px")};
`;

const CardNumber = styled(Text)`
  &.MuiTypography-root {
    font-weight: 600;
    transform: translateY(-1px);
    color: white;

    span {
      font-size: 14px;
      font-weight: 400;
      opacity: 0.5;
      margin-right: 2px;
      display: inline-block;
      transform: translateY(-1px);
    }
  }
`;

const CardInfoElement = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-gap: 2px;
`;

const ActiveChip = styled(Chip)`
  font-weight: 600;
  font-size: 13px;
`;

interface CardElementProps {
  issuer: CreditCardIssuer;
  compact?: boolean;
}

const CardElement = styled.div<CardElementProps>`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.compact ? "flex-end" : "space-between")};
  min-width: ${(props) => (props.compact ? "120px" : "250px")};
  background: ${(props) => CreditCardIssuerStyles[props.issuer].background};
  border-radius: 6px;
  padding: 16px;

  ${CardNumber} {
    &.MuiTypography-root {
      font-size: ${(props) => (props.compact ? "15px" : "16px")};
    }

    span {
      font-weight: ${(props) => (props.compact ? 600 : null)};
    }
  }

  img {
    width: ${(props) => (props.compact ? "30px" : "50px")};
    position: ${(props) => (props.compact ? "absolute" : null)};
    margin-right: 24px;
    top: 8px;
    left: 8px;
  }
`;

const CardActions: FC<{
  paymentMethod: ServerStripe.PaymentMethod;
}> = (props) => {
  const defaultPaymentMethodId = useDefaultPaymentMethodId();
  const { t, ready } = useTranslation("account");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { actions } = useContext(PaymentContext);

  const open = (event) => setAnchorEl(event.currentTarget);
  const close = () => setAnchorEl(null);

  const setAsDefault = () => {
    close();
    actions.setDefaultPaymentMethod(props.paymentMethod.id);
  };

  const remove = () => {
    close();
    actions.removePaymentMethod(props.paymentMethod.id);
  };

  const isActiveCard = props.paymentMethod.id === defaultPaymentMethodId;

  if (!ready) {
    return null;
  }

  return (
    <div>
      <IconButton onClick={open} size="small">
        <KvixIcon.Menu.Dots color="inherit" />
      </IconButton>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        TransitionComponent={Fade}
      >
        <MenuItem onClick={setAsDefault} disabled={isActiveCard}>
          {t("account.paymentMethod.cardInfo.setAsActive")}
        </MenuItem>
        <MenuItem onClick={remove}>
          {t("account.paymentMethod.cardInfo.removeCard")}
        </MenuItem>
      </Menu>
    </div>
  );
};

interface CardProps {
  card: ServerStripe.PaymentMethod.Card;
  compact?: boolean;
}

const Card: FC<CardProps> = (props) => {
  const { brand, last4 } = props.card;
  const issuer = brand as CreditCardIssuer;

  return (
    <CardElement issuer={issuer} compact={props.compact}>
      <img alt="" src={CreditCardIcon[brand]} />

      <CardNumber nowrap unselectable compact={props.compact}>
        {props.compact ? (
          <span>...&nbsp;</span>
        ) : (
          <span>••••&nbsp;••••&nbsp;••••&nbsp;</span>
        )}
        {last4}
      </CardNumber>
    </CardElement>
  );
};

interface CardInfoProps {
  paymentMethod: ServerStripe.PaymentMethod;
  compact?: boolean;
}

const CardInfo: FC<CardInfoProps> = (props) => {
  const { exp_month, exp_year } = props.paymentMethod.card;
  const { t, ready } = useTranslation("account");
  const now = new Date();
  const created = new Date(props.paymentMethod.created * 1000);
  const isExpired = isBefore(now, created);

  if (!ready) {
    return null;
  }

  const getValidText = () => {
    if (props.compact) {
      return t("account.paymentMethod.cardInfo.validTextCompact");
    }

    return t("account.paymentMethod.cardInfo.validText", {
      validity: `${exp_month}/${exp_year}`,
    });
  };

  const getExpiredText = () => {
    if (props.compact) {
      return t("account.paymentMethod.cardInfo.expiredTextCompact");
    }

    return t("account.paymentMethod.cardInfo.expiredText", {
      expiration: `${exp_month}/${exp_year}`,
    });
  };

  return (
    <CardInfoElement>
      {!isExpired && (
        <StatusText
          text={getValidText()}
          severity="success"
          iconRight={<KvixIcon.Checkmark.Simple style={{ marginTop: -1 }} />}
        />
      )}

      {isExpired && (
        <StatusText
          text={getExpiredText()}
          severity="error"
          iconRight={<KvixIcon.Error />}
        />
      )}

      {!props.compact && (
        <Text variant="body2" color="textSecondary" weight={500} nowrap>
          {t("account.paymentMethod.cardInfo.added", {
            addDate: format(created, "dd/MM/yyyy"),
          })}
        </Text>
      )}
    </CardInfoElement>
  );
};

export const CardPreviewRow: FC<Props> = (props) => {
  const defaultPaymentMethodId = useDefaultPaymentMethodId();
  const theme = useTheme();
  const isMobileDown = useMediaQuery(theme.breakpoints.down("xs"));
  const isTinyMobileDown = useMediaQuery(theme.breakpoints.down(400));
  const { t, ready } = useTranslation("account");

  const { paymentMethod } = props;

  if (typeof paymentMethod === "string") {
    return null;
  }

  const isDefault = defaultPaymentMethodId === paymentMethod.id;

  if (!ready) {
    return null;
  }

  return (
    <Container compact={isTinyMobileDown}>
      {isTinyMobileDown && (
        <Box visibility={isDefault ? "visible" : "hidden"}>
          <Tooltip
            arrow
            placement="top"
            title={t("account.planDetails.active")}
          >
            <KvixIcon.Checkmark.Circled color="primary" />
          </Tooltip>
        </Box>
      )}

      <Card card={paymentMethod.card} compact={isMobileDown} />
      <CardInfo paymentMethod={paymentMethod} compact={isMobileDown} />

      {!isTinyMobileDown && (
        <Box visibility={isDefault ? "visible" : "hidden"}>
          <ActiveChip color="primary" label={t("account.planDetails.active")} />
        </Box>
      )}

      <CardActions paymentMethod={paymentMethod} />

      {/* <Preview
        name={paymentMethod.billing_details.name}
        expiry={`${paymentMethod.card.exp_month}/${paymentMethod.card.exp_year}`}
        issuer={paymentMethod.card.brand}
        number={`**** **** **** ${paymentMethod.card.last4}`}
        cvc={"000"}
        preview
      /> */}
    </Container>
  );
};
